import { Injectable } from "@angular/core";
import { AlertController, ToastController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { OrderModel } from "src/app/models/order.model";
import { ProductModel } from "src/app/models/product.model";
import { TourneeModel } from "src/app/models/tournee.model";
import VAT from "../../../assets/data/VAT.json";
import { DataService } from "../data/data.service";
import { MathService } from "../math/math.service";
import { OrderService } from "../order/order.service";

@Injectable({
  providedIn: "root"
})
export class ProductService {

  canStartTourneeStatuses = ["open", "full", "distributing", "prepared"];
  notDeliveredOrderStatuses = ["created", "acknowledged", "accepted", "prepared"];
  typeFilterArray = [{name:"vegetable-producer", status:false},{name:"fruit-producer",status:false},{name:"butcher",status:false},
                  {name:"poultry",status:false},{name:"fisherman",status:false},{name:"baker",status:false},{name:"miller",status:false},
                  {name:"egger",status:false},{name:"dairy",status:false},{name:"cheese-maker",status:false},{name:"beekeeper",status:false},
                  {name:"wine-maker",status:false},{name:"brewery",status:false},{name:"snack",status:false},{name:"biscuit",status:false},
                  {name:"can",status:false},{name:"ice-cream",status:false},{name:"yogurt",status:false},{name:"condiment",status:false},
                  {name:"jam",status:false},{name:"oil",status:false},{name:"restaurant",status:false},{name:"cosmetic",status:false},{name:"flower",status:false}];
  typeFilterSelectedArray = [];
  producerFilterArray = [];

  quantityMetricsToType={
    piece:"piece",
    kilogram:"weight",
    '100gram':"weight",
    bottle:"piece",
    can:"piece",
    jar:"piece",
    bag:"piece",
    gram:"weight",
    slice:"piece",
    liter:"volume",
    bunch:"piece",
    person:"piece",
    basket:"piece",
    package:"piece",
    crate:"piece"
  };

  constructor(private dataService:DataService, private toastCtrl:ToastController, private mathService:MathService,
    private translateService:TranslateService, private alertCtrl:AlertController, private orderService:OrderService) {}


  convertQuantity(quantity:number, metric:string, exact:boolean=true){
    let display:string = exact?'':'±';
    if(!quantity){
      return;
    }
    if(metric =='gram'){
      if(quantity >= 1000){
        display += this.mathService.convertGrToKgStr(quantity) 
        display += " "+this.translateService.instant('quantityMetric.short.kilogram');
      }
      else{
        display += quantity;
        display += " "+this.translateService.instant('quantityMetric.short.gram');
      }
    }
    else{ 
      display += quantity;
      display += " " + (quantity>1?this.translateService.instant('quantityMetric.short.plural.'+metric):this.translateService.instant('quantityMetric.short.'+metric));
    } 
    return display;
  }

  convertCurrencyQuantity(price, currency, metric){
    return this.convertCurrency(price,currency) + ' / ' + this.translateService.instant('quantityMetric.short.'+metric);
  }

  convertCurrency(amount:number, currency:string){
    return this.translateService.instant('currencies.symbol.'+currency)+amount;
  }

  sortProduct(sortingTypeExclusions:string[] = []) {
    return function(a:ProductModel,b:ProductModel) {
      if(!sortingTypeExclusions.includes('icon')){
        if(a.icons && (a.icons.new || a.icons.favorite || a.icons.discount) && !b.icons ){
          return -1
        }
        else if(b.icons && (b.icons.new || b.icons.favorite || b.icons.discount) && !a.icons ){
          return 1;
        }      
      }
      if(!sortingTypeExclusions.includes('type') && a.details.type!==undefined && a.details.type!==null && b.details.type!==undefined && b.details.type!==null){
        // console.log('order by rank', a, b);
        return (a.details.type < b.details.type?1:-1);
      }
      if(!sortingTypeExclusions.includes('category') && a.details.category && b.details.category){
        // console.log('order by category');
        const compareCategory = a.details.category.name.localeCompare(b.details.category.name,'fr', { sensitivity: 'base' });
        if(compareCategory == 1){
          return 1
        }
        else if(compareCategory == -1){
          return -1
        }
      }
      if(!sortingTypeExclusions.includes('rank') && a.details.rank!==undefined && a.details.rank!==null && b.details.rank!==undefined && b.details.rank!==null){
        // console.log('order by rank', a, b);
        return a.details.rank - b.details.rank;
      }

      // console.log('product by name', this.clearForSorting(a.details.name), 
      // this.clearForSorting(b.details.name),this.clearForSorting(a.details.name) > this.clearForSorting(b.details.name),
      // a.details.name.localeCompare(b.details.name,'fr', { sensitivity: 'base' }));
      return a.details.name.localeCompare(b.details.name,'fr', { sensitivity: 'base' });
    }
  }

  transformProductsToArray(tournee: TourneeModel, status?:string,sortingTypeExclusions?:string[]): ProductModel[] {
    //merge products ordered with product list and sum quantity
    const products = tournee.products.references || {};
    const productsArrayQuantityReset:any = Object.entries(products)
    const orders:OrderModel[] = this.orderService.sortAndTransformToArray(tournee);
    for(const product of productsArrayQuantityReset){
      if(product[1].basket) { 
        products[product[0]].basket.quantity = product[1].basket.quantity = 0;
      }
    }
    const reducer2 = function (accumulator, currentValue) {
      if (!accumulator[currentValue.key] || currentValue.basket.status =='rejected' || currentValue.basket.status =='cancelled'){
      }
      else{
        const quantity = currentValue.basket.volumeDelivered || currentValue.basket.quantityDelivered || currentValue.basket.quantity;
        const metric = accumulator[currentValue.key].details.volumeDeliveredMetric || accumulator[currentValue.key].details.quantityMetric;
        if (!accumulator[currentValue.key].basket) {
          accumulator[currentValue.key].basket = { quantity: 0 };
        }
        accumulator[currentValue.key].basket.quantity += parseFloat(quantity);

        if(!accumulator[currentValue.key].sizings){
          accumulator[currentValue.key].sizings = {};
        }
        if(!accumulator[currentValue.key].sizings[quantity]){
          accumulator[currentValue.key].sizings[quantity] = {quantity:1, sizing:quantity,metric:metric};
        }
        else{
          accumulator[currentValue.key].sizings[quantity].quantity +=1;
        }

      }
      return accumulator;
    };
    // transform into a table of all the products ordered
    const reducer = function (accumulator, currentValue) {
      if(currentValue.products && currentValue.products.references){
        const products = Object.entries(currentValue.products.references).map(item => { 
          const product:ProductModel = {key: item[0], ...item[1] as {}};
          product.basket.status = currentValue.status;
          product.basket.name = currentValue.consumer?.details?.shortDetails?.name || 'anonymous';
          product.basket.orderKey = currentValue.key;
          return product;
        });
        accumulator = accumulator.concat(products);
      }
      return accumulator;
    };
    // create a list of product ordered by looping on the orders
    const productsOrdered = orders.reduce(reducer, []);
    //
    let productsTransformed = productsOrdered.reduce(reducer2, products||{});
    let productArray = Object.entries(productsTransformed).map(item => <ProductModel>{ key: item[0], ...item[1] as {} });
    if(status){
      productArray = productArray.filter(item => item.status == status || (item.basket && item.basket.quantity>0));
    }
    // console.log(productArray);
    return productArray.sort(this.sortProduct(sortingTypeExclusions));
  }

  sortAndTransformTourneesIntoProductListArrayByType(tournees:TourneeModel[]) {
    const products = { all:[], type:{}};
    for (const tournee of tournees) {
      const producer = tournee.producer;
      if (tournee.products && tournee.products.references) {
        tournee.products.referencesArray = [];
        for (const [key, product] of Object.entries(tournee.products.references)) {
          const item: ProductModel = { key: key, ...product as {}, tournee: tournee };
          tournee.products.referencesArray.push(item);
          products.all.push(item);
          if (!products.type[item.details.type]) {
            products.type[item.details.type] = { 
              name: [item.details.type], 
              nameTranslated: this.translateService.instant('product.types.'+item.details.type),
              array: [], rank:1 };
          }
          products.type[item.details.type].array.push(item);
        }
      }
    }
    return products;
  }

  sortAndTransformProductArrayIntoProductArrayByType(productArray:ProductModel[]) {
    const products = { all:[], type:{}};
    for (const item of productArray) {
      products.all.push(item);
      if (!products.type[item.details.type]) {
        products.type[item.details.type] = { 
          name: [item.details.type], 
          nameTranslated: this.translateService.instant('product.types.'+item.details.type),
          array: [], rank:1 };
      }
      products.type[item.details.type].array.push(item);
    }
    return products;
  }

  addVATExcludedPrice(product:ProductModel, countryCode: string){
    product.details.VATRate = product.details.VATRate || this.getVATRate(product, countryCode);
    product.details.unitPriceVATExcluded = product.details.unitPriceVATExcluded || this.mathService.calculateVATExcludedPrice(product.details.unitPriceVATIncluded, product.details.VATRate, product.details.currency);    ;
    product.details.VAT = this.mathService.substract(product.details.unitPriceVATIncluded, product.details.unitPriceVATExcluded);
    return product;
  }

  getVATRate(product:ProductModel, countryCode: string){
    // Garde : VAT.json n'a que certains pays (FRA/BEL/GBR) et countryCode peut
    // être absent ou en 2 lettres ('FR') -> VAT[countryCode] undefined -> plante.
    return product.details.VATRate || (VAT[countryCode] && VAT[countryCode][product.details.type]) || 0;
  }

  identifyIncludedProducts(products: ProductModel[], tournee: TourneeModel): ProductModel[] {
    if (tournee.products && tournee.products.references) {
      products.forEach(item => {
        if(!tournee.products.references[item.key]){
          item.status = 'notIncluded';
        }
        else{
          item.status = tournee.products.references[item.key].status;
          item.details.unitPriceVATIncluded = tournee.products.references[item.key].details.unitPriceVATIncluded;
          item.stock = tournee.products.references[item.key].stock || null;
        }
      });
      return products;
    } else {
      products.forEach(item => {
        item.status = "notIncluded";
      });
      return products;
    }
  }

  filter(filters:string[], products:ProductModel[]){
    return products.filter(product => {
      if(filters.length>0){
        return filters.includes(product.details.type)
      }
      else{
        return true;
      }
    })
  }

  resetStatusToProductArray(products:ProductModel[], status:string, statusName:string){
    products.forEach(product => product[statusName] = status);
    return products;
  }

  clearForSorting(input:string):string{
    return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  }

  search(products:ProductModel[],filter?:string){
    // console.log(products,filter);
    if(filter && filter.length>2){
      return products.filter(product => {
        // const similarity =  stringSimilarity.compareTwoStrings(this.clearForSorting(product.details.name.toLowerCase()),this.clearForSorting(filter.toLowerCase()));
        // return similarity  > 0.50;
        return this.clearForSorting(product.details.name).includes(this.clearForSorting(filter));
      });
    }
    else{
      return products;
    }
  }
  filterAndSearch(products:ProductModel[],keyword?:string | null, typesFilterArray?:string[]|[]){
    const filteredItems =  this.search(this.filter(typesFilterArray,products),keyword);
    return filteredItems
  }

  mergeWithBasket(products:ProductModel[], order?:OrderModel, countryCode?:string): ProductModel[]{
    return products.map(item => 
      <ProductModel>{
        ...this.addVATExcludedPrice(<ProductModel> {key: item[0], ...item[1] as {} }, countryCode), 
        basket:order.products.references[item.key] && order.products.references[item.key].basket?order.products.references[item.key].basket: null })
  }

  transformSaleProductsToArray(products: any, order?:OrderModel, countryCode?:string): ProductModel[] {
    let productArray = []
    if(order && order.products && order.products.references){
      productArray =  Object.entries(products).map(item => <ProductModel>{ 
        ...this.addVATExcludedPrice(<ProductModel> {key: item[0], ...item[1] as {} }, countryCode), 
        basket:order.products.references[item[0]] && order.products.references[item[0]].basket?order.products.references[item[0]].basket: null })
        .sort(this.sortProduct());
    }
    else{
      productArray =  Object.entries(products).map(function(item){ 
          return this.addVATExcludedPrice(<ProductModel> {key: item[0], ...item[1] as {} }, countryCode)
        }).sort(this.sortProduct());
    }

    return productArray;
  }

}
